<template>
  <!-- Header -->
  <div class="flex items-center justify-between gap-2">
    <div class="text-lg font-semibold">
      {{ percentageElapsed }}
      <Icon
        type="percent"
        class="text-sm text-quaternary"
      />
      Elapsed
    </div>
    <div class="flex items-center gap-2">
      <Icon
        type="hourglass-end"
        class="text-sm text-quaternary"
      />
      <div class="text-lg font-semibold text-quaternary">
        {{ timeRemaining }}
      </div>
    </div>
  </div>

  <!-- Progress Bar -->
  <div
    class="mt-6 relative"
    aria-label="term progress"
  >
    <!-- 12 & 6 month markers -->
    <div
      v-if="markerPositions.twelvePercent >= 0 && markerPositions.twelvePercent <= 100"
      v-tippy="{ content: markerPositions.twelveDate }"
      class="term-track-marker"
      :style="'left:' + markerPositions.twelvePercent + '%;'"
    >
      12m
    </div>
    <div
      v-if="markerPositions.sixPercent >= 0 && markerPositions.sixPercent <= 100"
      v-tippy="{ content: markerPositions.sixDate }"
      class="term-track-marker"
      :style="'left:' + markerPositions.sixPercent + '%;'"
    >
      6m
    </div>
    <div class="flex w-full items-center justify-between">
      <span
        v-for="(isElapsed, idx) in monthProgress.steps"
        :key="idx"
        class="relative block shrink-0"
        :class="dotSizeClass"
      >
        <span
          v-if="isElapsed && idx === currentElapsedIndex"
          class="heartbeat absolute inset-0 rounded-full bg-brand-solid"
        ></span>
        <span
          class="block h-full w-full rounded-full"
          :class="isElapsed ? 'bg-brand-solid' : 'bg-quaternary'"
        ></span>
      </span>
    </div>
  </div>

  <!-- Dates -->
  <div class="flex justify-between mt-2">
    <div>
      <div class="text-xs text-quaternary">Start</div>
      <div class="text-sm font-semibold">{{ dayjs(startDate).format('D MMM YY') }}</div>
    </div>
    <div class="text-right">
      <div class="text-xs text-quaternary">End</div>
      <div class="text-sm font-semibold">{{ dayjs(endDate).format('D MMM YY') }}</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

// vendors
import dayjs from 'dayjs'

// components
import Icon from '@/components/icon/Icon.vue'

const props = defineProps({
  startDate: {
    type: String,
    default: null
  },
  endDate: {
    type: String,
    default: null
  }
})

/**
 * Size classes for dots. Chosen from Tailwind spacing scale present in project
 * to guarantee availability and keep circles from squishing.
 */
const DOT_SIZE_SMALL = 'h-1 w-1'
const DOT_SIZE_MEDIUM = 'h-2 w-2'
const DOT_SIZE_LARGE = 'h-3 w-3'

/** Shape describing progress across months. */
type MonthProgress = {
  totalMonths: number
  elapsedMonths: number
  steps: boolean[]
}

/**
 * Compute the number of months between start and end, and which months have elapsed.
 * Includes defensive fallbacks and caps the total to avoid rendering hundreds of nodes.
 */
const monthProgress = computed<MonthProgress>(() => {
  try {
    const fallbackTotal = 24
    const now = dayjs()
    const start = props.startDate ? dayjs(props.startDate) : null
    const end = props.endDate ? dayjs(props.endDate) : null

    let totalMonths = start && end ? Math.max(1, end.diff(start, 'month') + 1) : fallbackTotal
    // Cap to a reasonable maximum to maintain performance on very long terms
    totalMonths = Math.min(84, totalMonths)

    // Use end if the term has finished; otherwise use now. Avoids needing plugins.
    const comparisonPoint = end && now.isAfter(end) ? end : now
    const rawElapsed = start ? comparisonPoint.diff(start, 'month') + 1 : Math.floor(totalMonths * 0.5)
    const elapsedMonths = Math.max(0, Math.min(totalMonths, rawElapsed))

    const steps = Array.from({ length: totalMonths }, (_, i) => i < elapsedMonths)

    return { totalMonths, elapsedMonths, steps }
  } catch (error) {
    console.error('Failed to compute month progress', error)
    const totalMonths = 24
    const elapsedMonths = 12
    return {
      totalMonths,
      elapsedMonths,
      steps: Array.from({ length: totalMonths }, (_, i) => i < elapsedMonths)
    }
  }
})

/** Index of the last filled month (current month in progress). */
const currentElapsedIndex = computed<number>(() => {
  try {
    return Math.max(0, monthProgress.value.elapsedMonths - 1)
  } catch (error) {
    console.error('Failed to compute current elapsed index', error)
    return 0
  }
})

/**
 * Pick a dot size based on total months so dots always fit on one row for
 * common terms (12–84) without stretching.
 */
const dotSizeClass = computed<string>(() => {
  try {
    const total = monthProgress.value.totalMonths
    if (total <= 26) return DOT_SIZE_LARGE
    if (total <= 50) return DOT_SIZE_MEDIUM
    return DOT_SIZE_SMALL
  } catch (error) {
    console.error('Failed to compute dot size', error)
    return DOT_SIZE_MEDIUM
  }
})

/**
 * Calculate the percentage of the term that has elapsed.
 * Returns a rounded integer percentage (0-100).
 */
const percentageElapsed = computed<number>(() => {
  try {
    if (!props.startDate || !props.endDate) {
      return 0
    }

    const now = dayjs()
    const start = dayjs(props.startDate)
    const end = dayjs(props.endDate)

    // Calculate total term duration in days
    const totalDays = end.diff(start, 'day')

    if (totalDays <= 0) {
      return 100
    }

    // Use end date if term has finished; otherwise use now
    const comparisonPoint = now.isAfter(end) ? end : now
    const elapsedDays = comparisonPoint.diff(start, 'day')

    // Calculate percentage and round to nearest integer
    const percentage = Math.round((elapsedDays / totalDays) * 100)

    // Clamp between 0 and 100
    return Math.max(0, Math.min(100, percentage))
  } catch (error) {
    console.error('Failed to compute percentage elapsed', error)
    return 0
  }
})

/**
 * Format time remaining until end date.
 * Shows months by default (e.g., "38m"). If less than 6 months, shows months and days (e.g., "4m 15d").
 */
const timeRemaining = computed<string>(() => {
  try {
    if (!props.endDate) {
      return ''
    }

    const now = dayjs()
    const end = dayjs(props.endDate)

    // If end date is in the past, return expired message
    if (end.isBefore(now)) {
      return 'Expired'
    }

    // Calculate months remaining
    const monthsRemaining = end.diff(now, 'month', true)

    // If less than 6 months, show months and days
    if (monthsRemaining < 6) {
      const fullMonths = Math.floor(monthsRemaining)
      const remainingDays = end.diff(now.add(fullMonths, 'month'), 'day')
      return `${fullMonths}m ${remainingDays}d`
    }

    // Show months only when >= 6 months
    const fullMonths = Math.floor(monthsRemaining)
    return `${fullMonths} months`
  } catch (error) {
    console.error('Failed to compute time remaining', error)
    return ''
  }
})

/**
 * Calculate positions and dates for 12m and 6m markers above the progress bar.
 * Positions are calculated as percentages from the start date.
 */
const markerPositions = computed(() => {
  try {
    const start = props.startDate ? dayjs(props.startDate) : null
    const end = props.endDate ? dayjs(props.endDate) : null

    if (!start || !end) {
      return {
        twelvePercent: -1,
        sixPercent: -1,
        twelveDate: '',
        sixDate: ''
      }
    }

    // Calculate term in days
    const termInDays = end.diff(start, 'day')

    // Calculate 12 months before end date
    const twelveDate = dayjs(props.endDate).subtract(12, 'month').format('DD/MM/YYYY')
    const twelvePercent = ((termInDays - 365) / termInDays) * 100

    // Calculate 6 months before end date
    const sixDate = dayjs(props.endDate).subtract(6, 'month').format('DD/MM/YYYY')
    const sixPercent = ((termInDays - 182.5) / termInDays) * 100

    return {
      twelvePercent: Math.max(0, Math.min(100, twelvePercent)),
      sixPercent: Math.max(0, Math.min(100, sixPercent)),
      twelveDate,
      sixDate
    }
  } catch (error) {
    console.error('Failed to compute marker positions', error)
    return {
      twelvePercent: -1,
      sixPercent: -1,
      twelveDate: '',
      sixDate: ''
    }
  }
})
</script>

<style>
@keyframes heartbeat-scale {
  0% {
    transform: scale(1);
    opacity: 0.6;
  }
  60% {
    transform: scale(4);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 0;
  }
}

.heartbeat {
  animation: heartbeat-scale 1.8s ease-in-out infinite;
}

.term-track-marker {
  position: absolute;
  top: -18px;
  font-size: var(--text-xs);
  padding-bottom: var(--s-1);
  color: var(--text-quaternary);
  transform: translateX(-50%);
  white-space: nowrap;
}
</style>
